import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api } from '../../api'
import { META_KEYS, db, metaGet, metaSet } from '../../db/db'
import { AuthContext, type AuthContextValue, type AuthState } from './authContext'

/**
 * The login stub — no real IdP yet, but it exercises the token model of §8 so
 * that the shape is settled before production auth lands.
 *
 * Production intent (§8.1): with `app.scio.cz` and `api.scio.cz` sharing the
 * registrable domain, the access token lives **in memory** and the refresh
 * token in an `HttpOnly; Secure; SameSite=Lax` cookie. The mobile build uses
 * bearer tokens instead (§13.2 difference 3) — hence `AUTH_STRATEGY`.
 *
 * The stub deviates in exactly one way, deliberately: the stub token is
 * persisted, because checking that iOS storage survives eight days idle means
 * reopening the app offline, and an in-memory token would land that check on
 * the login screen instead of on the stored data. The deviation cannot affect
 * an in-progress session anyway — it is authorised by its session grant, not
 * by the access token.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthState | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    void (async () => {
      const stored = await metaGet<AuthState>(META_KEYS.auth)
      if (stored) {
        api.setAccessToken(stored.accessToken)
        setAuth(stored)
      }
      setLoading(false)
    })()
  }, [])

  const login = useCallback(async (displayName: string) => {
    const result = await api.loginStub(displayName)
    api.setAccessToken(result.accessToken)

    const next: AuthState = {
      userId: result.user.id,
      displayName: result.user.displayName,
      accessToken: result.accessToken,
    }

    try {
      const me = await api.getMe()
      next.entitlement = me.entitlement
      await metaSet(META_KEYS.entitlement, me.entitlement)
    } catch {
      // Entitlement is server-authoritative; failing to read it here only
      // costs the shell some polish, never access to an in-progress session.
    }

    await metaSet(META_KEYS.auth, next)
    setAuth(next)
  }, [])

  const logout = useCallback(async () => {
    api.setAccessToken(null)
    await db.meta.delete(META_KEYS.auth)
    setAuth(null)
  }, [])

  const value = useMemo<AuthContextValue>(() => ({ auth, loading, login, logout }), [auth, loading, login, logout])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
